import Swiper from "swiper";
import { Navigation } from "swiper/modules";

const RESIZE_DEBOUNCE = 250;
const OVERFLOW_TOLERANCE = 1;
const FALLBACK_SPACE_BETWEEN = 12;
const SLIDER_CLASS = "is-slider";
const ARROW_TOP_PROPERTY = "--cqc-arrow-top";

class CollectionQuickCallouts extends HTMLElement {
  private swiper: Swiper | null = null;
  private container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback(): void {
    this.container = this.querySelector<HTMLElement>(".swiper-container");

    if (!this.container) {
      console.warn("Swiper container not found in collection-quick-callouts");
      return;
    }

    this.sync();
    this.setupResizeHandler();

    // Label widths change once the web fonts land, which can flip the state.
    document.fonts?.ready.then(() => this.sync());
  }

  disconnectedCallback(): void {
    this.cleanup();
  }

  private get slides(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>(".swiper-slide"));
  }

  // Mirrors `--cqc-gap` in collection-quick-callouts.scss, which is declared as
  // a plain pixel value so it can be read back here.
  private get spaceBetween(): number {
    const gap = parseFloat(
      getComputedStyle(this).getPropertyValue("--cqc-gap"),
    );

    return Number.isFinite(gap) ? gap : FALLBACK_SPACE_BETWEEN;
  }

  /**
   * Slides keep their CSS width whether or not Swiper is running, so the row
   * can be measured in both states without initialising/destroying to find out.
   */
  private overflows(): boolean {
    const slides = this.slides;

    if (!this.container || slides.length < 2) {
      return false;
    }

    const slidesWidth = slides.reduce(
      (total, slide) => total + slide.getBoundingClientRect().width,
      0,
    );
    const gapsWidth = (slides.length - 1) * this.spaceBetween;

    return (
      slidesWidth > 0 &&
      slidesWidth + gapsWidth > this.container.clientWidth + OVERFLOW_TOLERANCE
    );
  }

  /**
   * The arrows are centred on the tile image, not on the image + label, so the
   * offset is measured from the image itself — a collection name that wraps to
   * two lines must not drag them below the image centre.
   */
  private updateArrowOffset(): void {
    const media = this.querySelector<HTMLElement>(
      ".collection-quick-callouts__media",
    );

    if (!media) {
      return;
    }

    const { height } = media.getBoundingClientRect();

    if (height > 0) {
      this.style.setProperty(ARROW_TOP_PROPERTY, `${height / 2}px`);
    }
  }

  private sync(): void {
    // The legacy filter AJAX can replace this element between a resize/font
    // callback being queued and it running — never build a Swiper off-DOM.
    if (!this.isConnected) {
      return;
    }

    if (!this.overflows()) {
      this.destroySwiper();
      return;
    }

    // Rebuild when a breakpoint has changed the gap Swiper was built with.
    if (this.swiper && this.swiper.params.spaceBetween !== this.spaceBetween) {
      this.destroySwiper();
    }

    this.initSwiper();
    this.updateArrowOffset();
  }

  private initSwiper(): void {
    if (this.swiper || !this.container) {
      return;
    }

    const nextEl = this.querySelector<HTMLElement>(".slider-button--next");
    const prevEl = this.querySelector<HTMLElement>(".slider-button--prev");

    // Applied before Swiper is constructed so the arrows are never revealed
    // ahead of the slider they drive; removed again if the constructor throws.
    this.classList.add(SLIDER_CLASS);

    try {
      this.swiper = new Swiper(this.container, {
        modules: [Navigation],
        loop: false,
        grabCursor: true,
        slidesPerView: "auto",
        spaceBetween: this.spaceBetween,
        watchOverflow: true,
        threshold: 5,
        ...((nextEl || prevEl) && {
          navigation: { nextEl, prevEl },
        }),
      });
    } catch (error) {
      this.classList.remove(SLIDER_CLASS);

      console.error(
        "Error initializing Swiper in collection-quick-callouts:",
        error,
      );
    }
  }

  private destroySwiper(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.classList.remove(SLIDER_CLASS);
  }

  private setupResizeHandler(): void {
    this.resizeObserver = new ResizeObserver(() => {
      // Tile widths are CSS-driven and follow the viewport immediately, so the
      // arrow offset is re-read undebounced to keep the arrows on the image.
      this.updateArrowOffset();

      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = setTimeout(() => this.sync(), RESIZE_DEBOUNCE);
    });

    this.resizeObserver.observe(document.body);
  }

  private cleanup(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.destroySwiper();
  }
}

if (!customElements.get("collection-quick-callouts")) {
  customElements.define("collection-quick-callouts", CollectionQuickCallouts);
}

export default CollectionQuickCallouts;
